import { useState, useEffect, useRef } from 'react';

/**
 * useScrollSpy - Track which section is currently visible in the viewport.
 *
 * Uses scroll position to find the element closest to a target zone in the viewport.
 * This approach is more reliable than IntersectionObserver for small header elements.
 *
 * @param elementIds - Array of element IDs to observe
 * @param options - Configuration options
 * @returns The ID of the currently active element, or null if none visible
 */
export function useScrollSpy(
  elementIds: string[],
  options: {
    targetPosition?: number; // 0-1, where in viewport to target (0.15 = 15% from top)
    containerRef?: React.RefObject<HTMLElement | null>; // Optional scroll container (defaults to window)
  } = {}
): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);
  const lastActiveRef = useRef<string | null>(null);

  const { targetPosition = 0.15, containerRef } = options;

  useEffect(() => {
    if (elementIds.length === 0) {
      setActiveId(null);
      return;
    }

    // Function to find the element closest to target position
    const updateActiveId = () => {
      const container = containerRef?.current;
      const viewportHeight = container ? container.clientHeight : window.innerHeight;
      const targetY = viewportHeight * targetPosition;

      let bestMatch: { id: string; distance: number } | null = null;

      for (const id of elementIds) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          // If using a container, adjust rect relative to container
          const containerTop = container ? container.getBoundingClientRect().top : 0;
          const relativeTop = rect.top - containerTop;

          // Consider elements that are above or at the target line
          // (we want the most recent header we've scrolled past)
          if (relativeTop <= targetY + 50) {
            if (!bestMatch) {
              bestMatch = { id, distance: Math.abs(relativeTop - targetY) };
            } else {
              // Prefer the element that's closer to (but still above) the target
              const currentBestEl = document.getElementById(bestMatch.id);
              if (currentBestEl) {
                const currentBestRect = currentBestEl.getBoundingClientRect();
                const currentBestRelativeTop = currentBestRect.top - containerTop;
                if (relativeTop > currentBestRelativeTop) {
                  bestMatch = { id, distance: Math.abs(relativeTop - targetY) };
                }
              }
            }
          }
        }
      }

      // If no element is above the target, use the first one
      if (!bestMatch && elementIds.length > 0) {
        bestMatch = { id: elementIds[0], distance: 0 };
      }

      if (bestMatch && bestMatch.id !== lastActiveRef.current) {
        lastActiveRef.current = bestMatch.id;
        setActiveId(bestMatch.id);
      }
    };

    // Throttle scroll handler
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateActiveId();
          ticking = false;
        });
        ticking = true;
      }
    };

    const scrollTarget = containerRef?.current || window;
    scrollTarget.addEventListener('scroll', handleScroll, { passive: true });

    // Initial update
    updateActiveId();

    return () => {
      scrollTarget.removeEventListener('scroll', handleScroll);
    };
  }, [elementIds.join(','), targetPosition, containerRef]);

  return activeId;
}

export default useScrollSpy;
